/**
 * BetterAuth speaks English. The site does not.
 *
 * Every refusal from `@repo/auth/client` arrives as `{ code, message }`, and the
 * message is a developer-facing English string — "Invalid email", "User already
 * exists." A form that pastes it into a toast tells a Polish visitor, in the
 * middle of a Polish page, something they may not read at all; and even in
 * English "Invalid email" does not say what to do about it.
 *
 * So the `code` is translated and the `message` is never shown. That is the rule
 * this module exists to enforce: an unknown code falls back to the sentence the
 * CALL SITE supplied — which is written for that button, in Polish, and knows
 * what the visitor was trying to do. Passing the English text through as a
 * fallback would defeat the whole point.
 */

/**
 * Codes worth their own sentence, from `BASE_ERROR_CODES` and the email-OTP
 * plugin. Deliberately not the whole catalogue: a code that can only appear
 * when the app is misconfigured (`INVALID_ORIGIN`, `PROVIDER_NOT_FOUND`) has no
 * sentence a visitor could act on, so it takes the call site's fallback.
 */
const AUTH_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  // ── Sign-in ──────────────────────────────────────────────
  INVALID_EMAIL_OR_PASSWORD: 'Adres e-mail lub hasło się nie zgadzają.',
  INVALID_EMAIL: 'To nie wygląda na poprawny adres e-mail.',
  INVALID_PASSWORD: 'Hasło się nie zgadza.',
  USER_NOT_FOUND: 'Nie znaleźliśmy konta z tym adresem.',
  EMAIL_NOT_VERIFIED: 'Najpierw potwierdź adres e-mail — kod czeka w Twojej skrzynce.',
  CREDENTIAL_ACCOUNT_NOT_FOUND:
    'To konto zakładano przez logowanie społecznościowe. Użyj tego samego sposobu albo ustaw hasło przez „Zapomniałeś hasła?”.',

  // ── Sign-up ──────────────────────────────────────────────
  USER_ALREADY_EXISTS: 'Konto z tym adresem już istnieje. Zaloguj się albo zresetuj hasło.',
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL:
    'Konto z tym adresem już istnieje. Zaloguj się albo zresetuj hasło.',
  PASSWORD_TOO_SHORT: 'Hasło musi mieć co najmniej 8 znaków.',
  PASSWORD_TOO_LONG: 'To hasło jest za długie — skróć je.',
  FAILED_TO_CREATE_USER: 'Nie udało się założyć konta. Spróbuj ponownie za chwilę.',
  FAILED_TO_CREATE_SESSION: 'Konto powstało, ale nie udało się zalogować. Zaloguj się ręcznie.',

  // ── Links and codes ──────────────────────────────────────
  INVALID_TOKEN: 'Ten link jest nieprawidłowy albo został już użyty. Poproś o nowy.',
  TOKEN_EXPIRED: 'Ten link wygasł. Poproś o nowy.',
  INVALID_OTP: 'Ten kod się nie zgadza. Sprawdź go jeszcze raz albo poproś o nowy.',
  OTP_EXPIRED: 'Kod wygasł. Poproś o nowy.',
  TOO_MANY_ATTEMPTS: 'Za dużo prób. Poproś o nowy kod i spróbuj jeszcze raz.',
  EMAIL_ALREADY_VERIFIED: 'Ten adres jest już potwierdzony.',

  // ── Session and account ──────────────────────────────────
  SESSION_EXPIRED: 'Sesja wygasła. Zaloguj się jeszcze raz.',
  SESSION_NOT_FRESH: 'Ze względów bezpieczeństwa zaloguj się jeszcze raz i powtórz tę operację.',
  PASSWORD_ALREADY_SET: 'Hasło jest już ustawione — zmień je w ustawieniach bezpieczeństwa.',
  FAILED_TO_UNLINK_LAST_ACCOUNT:
    'To jedyny sposób logowania do tego konta — nie można go odłączyć.',
  SOCIAL_ACCOUNT_ALREADY_LINKED: 'To konto społecznościowe jest już połączone z innym kontem FLOU.',
  EMAIL_CAN_NOT_BE_UPDATED: 'Tego adresu nie da się już zmienić.',
}

/** How BetterAuth reports a refusal, narrowed to what a message can be built from. */
export type AuthErrorLike = {
  readonly code?: string | undefined
  readonly status?: number | undefined
}

/**
 * A Polish sentence for a BetterAuth refusal.
 *
 * `fallback` is what the visitor sees when the code is unknown, so write it for
 * the button being pressed ("Sprawdź adres e-mail i hasło.") rather than as a
 * generic apology.
 */
export function authErrorMessage(
  error: AuthErrorLike | null | undefined,
  fallback: string,
): string {
  if (!error) return fallback

  const known = error.code ? AUTH_ERROR_MESSAGES[error.code] : undefined
  if (known) return known

  // Rate limiting answers with a status and no code of its own — and it is the
  // one refusal where "try again" is actively wrong advice without "later".
  if (error.status === 429) return 'Za dużo prób. Odczekaj chwilę i spróbuj jeszcze raz.'

  return fallback
}
